import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, UserCheck, UserX, Copy, Trash2, Upload, Download, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AgencyRealtors() {
  const [realtors, setRealtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openResult, setOpenResult] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [hardDeleteFor, setHardDeleteFor] = useState(null); // { id, name } when confirming hard delete
  const [hardDeleteInput, setHardDeleteInput] = useState("");
  const [importFor, setImportFor] = useState(null); // { id, name } when opening import dialog
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [resetFor, setResetFor] = useState(null); // { id, name }
  const [resetPasswordInput, setResetPasswordInput] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [agency, setAgency] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/agency/realtors"),
      api.get("/agency/me"),
    ]).then(([r, m]) => {
      setRealtors(r.data.realtors);
      setAgency(m.data);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/agency/realtors", form);
      setOpenDialog(false);
      setForm({ name: "", email: "", phone: "", password: "" });
      setOpenResult(data);
      load();
      toast.success("Corretor cadastrado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar corretor");
    }
  };

  const toggleActive = async (id, active) => {
    await api.patch(`/agency/realtors/${id}?active=${active}`);
    load();
  };

  const remove = async (id, name, isActive) => {
    if (isActive) {
      // First step: soft delete (deactivate)
      const ok = window.confirm(`Desativar o corretor ${name}?\n\nEle não conseguirá mais acessar o painel, mas todos os leads e agenciamentos dele ficarão preservados no histórico. Você pode reativar depois clicando no ícone de check.`);
      if (!ok) return;
      await api.delete(`/agency/realtors/${id}`);
      toast.success(`${name} desativado`);
      load();
    } else {
      // Already inactive: open hard-delete dialog (safer than window.prompt)
      setHardDeleteFor({ id, name });
      setHardDeleteInput("");
    }
  };

  const confirmHardDelete = async () => {
    if (!hardDeleteFor) return;
    if (hardDeleteInput.trim() !== hardDeleteFor.name) {
      toast.error("Nome não confere. Exclusão cancelada.");
      return;
    }
    try {
      await api.delete(`/agency/realtors/${hardDeleteFor.id}?hard=true`);
      toast.success(`${hardDeleteFor.name} e todos os dados dele foram excluídos`);
      setHardDeleteFor(null);
      setHardDeleteInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  const uploadLeads = async () => {
    if (!importFor || !importFile) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("realtor_id", importFor.id);
    fd.append("file", importFile);
    try {
      const { data } = await api.post("/agency/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.imported_count} leads importados`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao importar");
    } finally {
      setImporting(false);
    }
  };

  const submitReset = async () => {
    if (!resetFor) return;
    setResetting(true);
    try {
      const body = resetPasswordInput.trim() ? { new_password: resetPasswordInput.trim() } : {};
      const { data } = await api.post(`/agency/realtors/${resetFor.id}/reset-password`, body);
      setResetResult(data);
      toast.success("Senha redefinida");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setResetting(false);
    }
  };

  const closeResetDialog = () => {
    setResetFor(null);
    setResetPasswordInput("");
    setResetResult(null);
  };

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="agency-realtors">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Equipe</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold mt-1">Corretores</h1>
          <p className="text-white/60 text-sm mt-2">
            {realtors.length} corretor{realtors.length !== 1 ? "es" : ""} na sua imobiliária.
            {agency?.max_realtors && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-xs font-mono" data-testid="realtor-quota">
                {realtors.length}/{agency.max_realtors}
              </span>
            )}
          </p>
        </div>
        <Button
          data-testid="add-realtor-btn"
          onClick={() => setOpenDialog(true)}
          disabled={agency?.max_realtors && realtors.length >= agency.max_realtors}
          className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white rounded-full px-5 disabled:opacity-50">
          <Plus className="w-4 h-4 mr-2" /> Cadastrar corretor
        </Button>
      </div>

      {agency?.max_realtors && realtors.length >= agency.max_realtors * 0.8 && (
        <div
          data-testid="quota-warning"
          className={`rounded-xl p-4 border ${realtors.length >= agency.max_realtors
            ? "bg-red-500/10 border-red-500/30 text-red-200"
            : "bg-amber-500/10 border-amber-500/30 text-amber-200"}`}>
          <div className="font-semibold">
            {realtors.length >= agency.max_realtors
              ? `Limite do plano atingido (${realtors.length}/${agency.max_realtors})`
              : `Você está perto do limite (${realtors.length}/${agency.max_realtors})`}
          </div>
          <div className="text-sm mt-1 opacity-90">
            Pra liberar mais vagas de corretor, entre em contato com o comercial:{" "}
            <a href="mailto:comercial@estatebrokeria.com" className="underline font-semibold">
              comercial@estatebrokeria.com
            </a>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-white/60">Carregando...</div>
      ) : realtors.length === 0 ? (
        <div className="p-12 text-center bg-[#0F1F3D] border border-dashed border-white/15 rounded-2xl">
          <div className="text-white/60 mb-4">Nenhum corretor cadastrado ainda.</div>
          <Button onClick={() => setOpenDialog(true)} className="bg-[#3B9EFF] text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Cadastrar primeiro corretor
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {realtors.map((r) => (
            <div key={r.id} className={`bg-[#0F1F3D] border rounded-2xl p-5 flex items-start gap-4 ${r.is_active ? "border-white/10" : "border-red-500/25 opacity-70"}`} data-testid={`realtor-card-${r.id}`}>
              <div className="w-12 h-12 rounded-full bg-[#3B9EFF] text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                {r.name[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{r.name}</div>
                <div className="text-xs text-white/50 truncate">{r.email}</div>
                <div className="text-xs text-white/50 mt-0.5">{r.phone || "sem telefone"}</div>
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span className="text-[#3B9EFF] font-semibold">{r.lead_count} leads</span>
                  <span className={r.is_active ? "text-emerald-400" : "text-red-400"}>{r.is_active ? "Ativo" : "Inativo"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => { setImportFor({ id: r.id, name: r.name }); setImportFile(null); setImportResult(null); }}
                  data-testid={`import-leads-${r.id}`}
                  className="p-2 rounded hover:bg-emerald-500/15 text-emerald-400" title="Importar leads (planilha)">
                  <Upload className="w-4 h-4" />
                </button>
                <button onClick={() => { setResetFor({ id: r.id, name: r.name }); setResetPasswordInput(""); setResetResult(null); }}
                  data-testid={`reset-password-${r.id}`}
                  className="p-2 rounded hover:bg-[#3B9EFF]/15 text-[#3B9EFF]" title="Redefinir senha">
                  <KeyRound className="w-4 h-4" />
                </button>
                <button onClick={() => toggleActive(r.id, !r.is_active)} data-testid={`toggle-realtor-${r.id}`}
                  className="p-2 rounded hover:bg-white/8 text-white/60" title={r.is_active ? "Desativar" : "Ativar"}>
                  {r.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(r.id, r.name, r.is_active)} data-testid={`delete-realtor-${r.id}`}
                  className="p-2 rounded hover:bg-red-500/15 text-red-400" title={r.is_active ? "Desativar" : "Excluir permanentemente"}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="bg-[#0F1F3D] border-white/10 text-white">
          <DialogHeader><DialogTitle>Cadastrar corretor</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><Label className="text-white/80">Nome</Label><Input data-testid="new-realtor-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40" /></div>
            <div><Label className="text-white/80">E-mail</Label><Input data-testid="new-realtor-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40" /></div>
            <div><Label className="text-white/80">WhatsApp (DDD)</Label><Input data-testid="new-realtor-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="48 99999-9999" className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40" /></div>
            <div><Label className="text-white/80">Senha inicial (opcional)</Label><Input data-testid="new-realtor-password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Deixe vazio para gerar automaticamente" className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpenDialog(false)} className="text-white/70">Cancelar</Button>
              <Button type="submit" data-testid="submit-new-realtor" className="bg-[#3B9EFF] text-white">Cadastrar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openResult} onOpenChange={() => setOpenResult(null)}>
        <DialogContent className="bg-[#0F1F3D] border-white/10 text-white">
          <DialogHeader><DialogTitle>Corretor cadastrado</DialogTitle></DialogHeader>
          {openResult && (
            <div className="space-y-3 text-sm">
              {openResult.whatsapp_sent ? (
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex gap-3">
                  <span className="text-2xl">📱</span>
                  <div>
                    <div className="font-semibold text-emerald-300">Mensagem de boas-vindas enviada!</div>
                    <div className="text-xs text-white/70 mt-1">
                      <b>{openResult.name}</b> vai receber no WhatsApp {openResult.phone} um convite explicando como definir a senha e ativar o WhatsApp.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-white/70">
                  Não conseguimos enviar o WhatsApp automaticamente. Copie a mensagem abaixo e envie manualmente pra {openResult.phone}.
                </div>
              )}
              <div className="bg-black/30 border border-white/10 rounded-lg p-4 font-mono text-xs space-y-1.5">
                <div><span className="text-white/50">E-mail:</span> {openResult.email}</div>
                <div><span className="text-white/50">Link de ativação:</span></div>
                <a href={openResult.setup_link} target="_blank" rel="noreferrer" className="text-[#3B9EFF] underline text-[11px] break-all block">{openResult.setup_link}</a>
              </div>
              <Button onClick={() => {
                const text = `Oi ${openResult.name.split(' ')[0]}! Sou o estatebroker.AI, seu novo assistente de IA e coach de vendas.\n\nDefine sua senha aqui: ${openResult.setup_link}\n\nDepois salva o número +55 48 9949-0057 na agenda e me manda um "oi" pra eu começar a trabalhar teus leads.`;
                navigator.clipboard.writeText(text);
                toast.success("Copiado! Pode reenviar manualmente se precisar.");
              }} variant="ghost" className="w-full text-white/70 hover:bg-white/5">
                <Copy className="w-4 h-4 mr-2" /> Copiar mensagem (opcional)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hard delete confirmation dialog */}
      <Dialog open={!!hardDeleteFor} onOpenChange={(open) => { if (!open) { setHardDeleteFor(null); setHardDeleteInput(""); } }}>        <DialogContent className="bg-[#0F1F3D] border-red-500/30 text-white max-w-md" data-testid="hard-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Excluir permanentemente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-white/80">
              Você está prestes a apagar <b>definitivamente</b> o corretor <b>{hardDeleteFor?.name}</b> e <b>TODOS os leads, agenciamentos e mensagens</b> dele.
            </p>
            <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-3">
              Esta ação não pode ser desfeita.
            </p>
            <div className="space-y-2">
              <Label className="text-xs text-white/60">Pra confirmar, digite o nome completo do corretor:</Label>
              <Input
                data-testid="hard-delete-name-input"
                value={hardDeleteInput}
                onChange={(e) => setHardDeleteInput(e.target.value)}
                placeholder={hardDeleteFor?.name || ""}
                className="bg-[#0A1937] border-white/10 text-white placeholder:text-white/30"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setHardDeleteFor(null); setHardDeleteInput(""); }} className="text-white/70">
              Cancelar
            </Button>
            <Button
              data-testid="hard-delete-confirm-btn"
              onClick={confirmHardDelete}
              disabled={hardDeleteInput.trim() !== hardDeleteFor?.name}
              className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-40"
            >
              Excluir tudo definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import leads dialog */}
      <Dialog open={!!importFor} onOpenChange={(open) => { if (!open) { setImportFor(null); setImportFile(null); setImportResult(null); } }}>
        <DialogContent className="bg-[#0F1F3D] border-white/10 text-white max-w-lg" data-testid="import-leads-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-400" />
              Importar leads para {importFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!importResult && (
              <>
                <div className="p-3 rounded-lg bg-[#0A1937] border border-white/10">
                  <div className="text-xs uppercase tracking-wider text-[#3B9EFF] font-bold mb-2">1. Baixe o template oficial</div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a href="/template_leads.xlsx" download
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/8 hover:bg-white/12 text-white text-sm font-medium">
                      <Download className="w-4 h-4" /> Template em branco
                    </a>
                    <a href="/template_leads_exemplo.xlsx" download
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/8 hover:bg-white/12 text-white text-sm font-medium">
                      <Download className="w-4 h-4" /> Com 20 exemplos
                    </a>
                  </div>
                  <div className="text-[11px] text-white/50 mt-2">A planilha tem uma aba <b>Instruções</b> explicando cada campo. Só Nome e WhatsApp são obrigatórios.</div>
                </div>

                <div className="p-3 rounded-lg bg-[#0A1937] border border-white/10">
                  <div className="text-xs uppercase tracking-wider text-[#3B9EFF] font-bold mb-2">2. Envie o arquivo preenchido</div>
                  <input
                    data-testid="import-file-input"
                    type="file"
                    accept=".xlsx,.csv"
                    onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                    className="text-sm text-white/80 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#3B9EFF] file:text-white file:cursor-pointer"
                  />
                  {importFile && (
                    <div className="text-xs text-emerald-300 mt-2">✓ {importFile.name} ({Math.round(importFile.size / 1024)} KB)</div>
                  )}
                </div>
              </>
            )}

            {importResult && (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="text-emerald-300 font-bold text-lg">✓ {importResult.imported_count} leads importados</div>
                  <div className="text-xs text-white/60 mt-1">Todos já aparecem no funil de {importResult.realtor_name}.</div>
                </div>
                {importResult.skipped_count > 0 && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="text-amber-300 font-semibold text-sm">{importResult.skipped_count} linhas puladas</div>
                    <div className="text-[11px] text-white/60 mt-2 max-h-32 overflow-y-auto space-y-1">
                      {importResult.skipped.map((s, i) => (
                        <div key={i}>Linha {s.row}: {s.reason}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {importResult ? (
              <Button onClick={() => { setImportFor(null); setImportFile(null); setImportResult(null); }} className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white">
                Fechar
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setImportFor(null)} className="text-white/70">Cancelar</Button>
                <Button
                  data-testid="import-leads-submit-btn"
                  onClick={uploadLeads}
                  disabled={!importFile || importing}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-40"
                >
                  {importing ? "Importando..." : "Importar agora"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reset password dialog */}
      <Dialog open={!!resetFor} onOpenChange={(open) => { if (!open) closeResetDialog(); }}>
        <DialogContent className="bg-[#0F1F3D] border-white/10 text-white max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#3B9EFF]">
              <KeyRound className="w-5 h-5" /> Redefinir senha de {resetFor?.name}
            </DialogTitle>
          </DialogHeader>
          {!resetResult ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-white/80">
                Escolha uma nova senha ou deixe em branco pra gerar automaticamente (10 caracteres aleatórios).
              </p>
              <div className="space-y-2">
                <Label className="text-white/80">Nova senha (opcional)</Label>
                <Input
                  data-testid="reset-password-input"
                  type="text"
                  value={resetPasswordInput}
                  onChange={(e) => setResetPasswordInput(e.target.value)}
                  placeholder="Deixe vazio pra gerar automaticamente"
                  className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40"
                  autoFocus
                />
                <p className="text-[11px] text-white/50">Mínimo 6 caracteres. Recomendado: senha temporária que ele possa trocar depois.</p>
              </div>
              <div className="p-3 rounded-lg bg-[#3B9EFF]/8 border border-[#3B9EFF]/20 text-xs text-white/70">
                Se o corretor tem WhatsApp cadastrado, a nova senha será enviada pra ele automaticamente.
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeResetDialog} className="text-white/70">Cancelar</Button>
                <Button
                  data-testid="reset-password-confirm-btn"
                  onClick={submitReset}
                  disabled={resetting || (resetPasswordInput.trim() !== "" && resetPasswordInput.trim().length < 6)}
                  className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white disabled:opacity-40"
                >
                  {resetting ? "Redefinindo..." : "Redefinir senha"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="text-emerald-300 font-bold text-lg mb-2">✓ Senha redefinida</div>
                <div className="text-xs text-white/70">Passe essa senha pro corretor. Ela não será exibida novamente.</div>
              </div>
              <div className="bg-black/30 border border-white/10 rounded-lg p-4 font-mono text-sm space-y-1.5">
                <div><span className="text-white/50">E-mail:</span> {resetResult.email}</div>
                <div><span className="text-white/50">Nova senha:</span> <span data-testid="reset-password-display" className="text-[#3B9EFF] font-bold text-base tracking-wider">{resetResult.new_password}</span></div>
              </div>
              {resetResult.whatsapp_sent ? (
                <div className="text-xs text-emerald-300 flex items-center gap-2">📱 Enviado no WhatsApp do corretor também.</div>
              ) : (
                <div className="text-xs text-amber-300 flex items-center gap-2">⚠️ Não conseguimos enviar via WhatsApp. Passe manualmente.</div>
              )}
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`Nova senha: ${resetResult.new_password}`);
                  toast.success("Copiado!");
                }}
                variant="ghost"
                className="w-full text-white/70 hover:bg-white/5"
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar senha
              </Button>
              <DialogFooter>
                <Button onClick={closeResetDialog} className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white">Fechar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
